/* eslint-disable no-param-reassign */
import { createTheme, alpha, lighten } from '@mui/material';
export { createTheme };
const defaultFont = ['"Source Sans Pro"', '"Open Sans"', '"Helvetica Neue"', "Arial", "sans-serif"].join(",");
const headingFont = ['Poppins', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(',');
export const createPixelTheme = _ref => {
  let {
    mui
  } = _ref;
  mui.typography = mui.typography || {};
  mui.typography.fontFamily = defaultFont;
  const defaultMuiTheme = createTheme(mui);
  if (defaultMuiTheme.palette.mode === 'light') {
    defaultMuiTheme.palette.text.primary = '#263238';
    defaultMuiTheme.palette.text.secondary = '#677074';
    defaultMuiTheme.palette.text.disabled = '#B0BEC5';
  }
  defaultMuiTheme.typography.h1.fontFamily = headingFont;
  defaultMuiTheme.typography.h1.color = defaultMuiTheme.palette.text.primary;
  defaultMuiTheme.typography.h1.fontSize = "2rem";
  defaultMuiTheme.typography.h1.fontWeight = 500;
  defaultMuiTheme.typography.h1.lineHeight = "3rem";
  defaultMuiTheme.typography.h2.fontFamily = headingFont;
  defaultMuiTheme.typography.h2.color = defaultMuiTheme.palette.text.primary;
  defaultMuiTheme.typography.h2.fontSize = "1.5rem"; //* 16px=36px
  defaultMuiTheme.typography.h2.fontWeight = 600;
  defaultMuiTheme.typography.h2.lineHeight = "2.375rem"; //* 36px=54px;

  defaultMuiTheme.typography.h3.fontFamily = headingFont;
  defaultMuiTheme.typography.h3.color = defaultMuiTheme.palette.text.primary;
  defaultMuiTheme.typography.h3.fontSize = "1.25rem"; //* 16px=32px
  defaultMuiTheme.typography.h3.fontWeight = 600;
  defaultMuiTheme.typography.h3.lineHeight = "2rem"; //* 32px=54px;

  defaultMuiTheme.typography.h4.fontFamily = headingFont;
  defaultMuiTheme.typography.h4.color = defaultMuiTheme.palette.text.primary;
  defaultMuiTheme.typography.h4.fontSize = "1rem"; //* 16px=28px
  defaultMuiTheme.typography.h4.fontWeight = 600;
  defaultMuiTheme.typography.h4.lineHeight = "1.75rem"; //* 32px=54px;

  defaultMuiTheme.typography.h5.fontFamily = headingFont;
  defaultMuiTheme.typography.h5.color = defaultMuiTheme.palette.text.primary;
  defaultMuiTheme.typography.h5.fontSize = "0.875rem";
  defaultMuiTheme.typography.h5.fontWeight = 600;
  defaultMuiTheme.typography.h5.lineHeight = "1.5rem";
  defaultMuiTheme.typography.h6.fontFamily = headingFont;
  defaultMuiTheme.typography.h6.color = defaultMuiTheme.palette.text.primary;
  defaultMuiTheme.typography.h6.fontSize = "0.875rem";
  defaultMuiTheme.typography.h6.fontWeight = 500;
  defaultMuiTheme.typography.h6.lineHeight = "1.5rem";
  defaultMuiTheme.typography.subtitle1.fontFamily = headingFont;
  defaultMuiTheme.typography.subtitle1.color = defaultMuiTheme.palette.text.primary;
  defaultMuiTheme.typography.subtitle1.fontSize = "0.875rem";
  defaultMuiTheme.typography.subtitle1.fontWeight = 600;
  defaultMuiTheme.typography.subtitle1.lineHeight = "1.5em";
  defaultMuiTheme.typography.subtitle2.fontFamily = headingFont;
  defaultMuiTheme.typography.subtitle2.color = defaultMuiTheme.palette.text.primary;
  defaultMuiTheme.typography.subtitle2.fontSize = "0.875rem";
  defaultMuiTheme.typography.subtitle2.fontWeight = 500;
  defaultMuiTheme.typography.subtitle2.lineHeight = "1.5em";
  defaultMuiTheme.typography.body1.fontFamily = defaultFont;
  defaultMuiTheme.typography.body1.color = defaultMuiTheme.palette.text.primary;
  defaultMuiTheme.typography.body1.fontSize = "0.875rem";
  defaultMuiTheme.typography.body1.fontWeight = 400;
  defaultMuiTheme.typography.body1.lineHeight = "1.5rem";
  defaultMuiTheme.typography.body2.fontFamily = defaultFont;
  defaultMuiTheme.typography.body2.color = lighten(defaultMuiTheme.palette.text.primary, 0.1);
  defaultMuiTheme.typography.body2.fontSize = "0.800rem";
  defaultMuiTheme.typography.body2.fontWeight = "normal";
  defaultMuiTheme.typography.body2.lineHeight = "1.5rem";
  defaultMuiTheme.typography.button.textTransform = "none";
  defaultMuiTheme.typography.button.fontFamily = defaultFont;
  defaultMuiTheme.typography.button.fontSize = "0.875rem";
  defaultMuiTheme.typography.button.fontWeight = 600;
  defaultMuiTheme.typography.button.lineHeight = "1.5rem";
  defaultMuiTheme.typography.overline.fontSize = "0.75rem";
  defaultMuiTheme.typography.overline.lineHeight = "1.25rem";
  defaultMuiTheme.typography.overline.fontWeight = 600;
  defaultMuiTheme.typography.overline.letterSpacing = '0.08em';
  defaultMuiTheme.typography.listCardHeading = {
    color: 'red',
    ...defaultMuiTheme.typography.body1
  };
  const {
    palette
  } = defaultMuiTheme;
  const isDarkMode = palette.mode === 'dark';
  const {
    info: defaultInfo,
    success: defaultSuccess,
    warning: defaultWarning,
    error: defaultError
  } = palette;
  const extendedInfoPalette = {
    ...defaultInfo,
    softBackground: alpha(defaultInfo.main, 0.1),
    softContrastText: isDarkMode ? lighten(defaultInfo.light, 0.3) : defaultInfo.dark,
    snackbarBackground: "#e7eaec"
  };
  const extendedSuccessPalette = {
    ...defaultSuccess,
    softBackground: alpha(defaultSuccess.main, 0.1),
    softContrastText: isDarkMode ? lighten(defaultSuccess.main, 0.4) : defaultSuccess.dark,
    snackbarBackground: "#e7f1ea"
  };
  const extendedWarningPalette = {
    ...defaultWarning,
    softBackground: alpha(defaultWarning.main, 0.1),
    softContrastText: isDarkMode ? lighten(defaultWarning.main, 0.3) : defaultWarning.dark,
    snackbarBackground: "#f4ece7"
  };
  const extendedErrorPalette = {
    ...defaultError,
    softBackground: alpha(defaultError.main, 0.1),
    softContrastText: isDarkMode ? lighten(defaultError.main, 0.3) : defaultError.dark,
    snackbarBackground: "#f2e4e5"
  };

  /*
  palette.info.softBackground = alpha(palette.info.main, 0.1)
  palette.info.softContrastText = isDarkMode ? lighten(palette.info.light, 0.3) : palette.info.dark;
  palette.success.softBackground = alpha(palette.success.main, 0.1)
  palette.success.softContrastText = isDarkMode ? lighten(palette.success.main, 0.4) : palette.success.dark;
  palette.warning.softBackground = alpha(palette.warning.main, 0.1)
  palette.warning.softContrastText = isDarkMode ? lighten(palette.warning.main, 0.3) : palette.warning.dark;
  palette.error.softBackground = alpha(palette.error.main, 0.1)
  palette.error.softContrastText = isDarkMode ? lighten(palette.error.main, 0.5) : palette.error.dark;
  */

  //! could cache all pixelContrast values if these two variables never change
  const pixelColor = isDarkMode ? '#ffffff' : '#000000';
  const pixelMode = isDarkMode ? '#000000' : '#ffffff';
  const generateContrastShade = amount => isDarkMode ? lighten(pixelColor, amount) : lighten(pixelColor, amount);
  const generateContrastFade = amount => isDarkMode ? alpha(pixelColor, amount) : alpha(pixelColor, amount);
  const pixelContrast = {
    shades: {
      smooth: generateContrastShade(0.04),
      softest: generateContrastShade(0.02),
      softer: generateContrastShade(0.1),
      soft: generateContrastShade(0.23),
      medium: generateContrastShade(0.5),
      hard: generateContrastShade(0.7),
      harder: generateContrastShade(0.89),
      hardest: generateContrastShade(0.98)
    },
    fades: {
      smooth: generateContrastFade(0.04),
      softest: generateContrastFade(0.02),
      softer: generateContrastFade(0.1),
      soft: generateContrastFade(0.23),
      medium: generateContrastFade(0.5),
      hard: generateContrastFade(0.7),
      harder: generateContrastFade(0.89),
      hardest: generateContrastFade(0.97)
    },
    color: pixelColor,
    mode: pixelMode,
    generateContrastShade,
    generateContrastFade
  };
  const dataViz = {
    teal: '#55BDC5',
    purple: '#4E49CB',
    gold: '#DB8B39',
    pink: '#C9458D',
    pastelPurple: '#8F8BF3',
    green: '#78DE7C',
    blue: '#4180E3',
    darkPurple: '#683FAB'
  };
  const pixelTheme = {
    palette: {
      info: extendedInfoPalette,
      success: extendedSuccessPalette,
      warning: extendedWarningPalette,
      error: extendedErrorPalette,
      pixelContrast,
      background: {
        secondary: '#F5F8FA'
      },
      dataViz,
      bubbleText: {
        background: "#757575",
        text: "#ffffff"
      },
      highlight: {
        background: "#f3f3f3",
        background_hover: "#f8f8f8",
        border: "#d8d8d8",
        text: "#000000"
      }
    },
    size: {
      focusWidth: 3
    }
  };
  const newPixelTheme = createTheme(defaultMuiTheme, pixelTheme);
  return newPixelTheme;
};
export default createPixelTheme;